import {PIXI} from '@colonist/pixi'
import {GameColor} from '../../GameColor.ts'
import {TimeUtils} from '../../../../../shared/utils/TimeUtils.ts'
import {SectionView} from '../../ui_canvas_engine/SectionView.ts'
import {SpriteView} from '../../ui_canvas_engine/SpriteView.ts'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {type UIGameMapView} from './UIGameMapView.ts'
import {UIGameMapDiceProbabilityView} from './UIGameMapDiceProbabilityView.ts'
import {type UIGameAnimation} from '../UIGameEventController.ts'
import {UIGameOpacityAnimation} from '../../ui_canvas_engine/UIGameOpacityAnimation.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type HexFace, Point} from '@colonist/game-mechanic-validators'
import {FOREGROUND_SCALE, FOREGROUND_Y_OFFSET_RATIO} from './UIGameMapTileConstants.ts'

export class UIGameMapTileView extends SectionView implements UIGameAnimation {
    private readonly mapView: UIGameMapView
    private readonly uiGameManager: UIGameManager
    private readonly opacityAnimation: UIGameOpacityAnimation
    private readonly tileWidth: number
    private readonly tileXOffset: number
    private readonly tileYOffset: number
    private whiteSurrounding: PIXI.Graphics | undefined
    private isStrongHighlighted = false

    hexFace: HexFace
    tileSpriteView: SpriteView
    tileForegroundSpriteView: SpriteView | undefined
    diceNumber: number | undefined
    probabilityView: UIGameMapDiceProbabilityView | undefined

    constructor(uiGameManager: UIGameManager, tileHex: HexFace, x: number, y: number, w: number, h: number, texture: PIXI.Texture, container: PIXI.Container, foregroundTexture?: PIXI.Texture) {
        const removePadding = true
        super(x, y, w, h, container, removePadding)
        this.mapView = uiGameManager.mapController.mapView
        this.uiGameManager = uiGameManager

        const smallScalingToOverlapGapsBetweenTiles = 1.015
        const tileHeight = this.height * smallScalingToOverlapGapsBetweenTiles
        this.tileWidth = this.width * smallScalingToOverlapGapsBetweenTiles
        this.tileXOffset = (this.width - this.tileWidth) / 2
        this.tileYOffset = (this.height - tileHeight) / 2

        this.tileSpriteView = new SpriteView(this.tileXOffset, this.tileYOffset, this.tileWidth, tileHeight, this, texture)

        if(foregroundTexture != undefined) {
            this.tileForegroundSpriteView = this.createForegroundSpriteView(foregroundTexture)
        }

        this.hexFace = tileHex
        this.zIndex = this.mapView.layers.mapTiles
        this.opacityAnimation = new UIGameOpacityAnimation()
    }

    onGameTick(deltaTime: number, elapsedMS: number) {
        if(this.whiteSurrounding == undefined) return
        this.opacityAnimation.animateOpacity(this.whiteSurrounding)
    }

    highlightTileForTime(highlightTimeInSeconds: number) {
        this.highlightWithWhiteSurrounding(true)
        const timeToHighlight = highlightTimeInSeconds * TimeUtils.MILLISECONDS
        setTimeout(() => {
            this.forceResetHighlightWithWhiteSurrounding()
        }, timeToHighlight)
    }

    highlightWithWhiteSurrounding(useDarkerHighlight: boolean = false) {
        if(useDarkerHighlight) this.isStrongHighlighted = true

        const tintColor = useDarkerHighlight ? GameColor.Color.GrayScale14 : GameColor.Color.GrayScale20
        this.tileSpriteView.tint = GameColor.getColorHexValue(tintColor)
        this.uiGameManager.eventController.registerUIGameAnimation(this)
        this.zIndex = this.mapView.layers.highlightedMapTiles
        this.drawWhiteSurrounding()
        this.mapView.sortChildren()
    }

    private drawWhiteSurrounding() {
        if(this.whiteSurrounding != undefined) this.whiteSurrounding.destroy()

        const radius = this.mapView.hexagonHeight / 2 - this.mapView.hexagonHeight * 0.01
        const startX = this.mapView.hexagonWidth / 2
        const startY = this.mapView.hexagonHeight / 2

        const hexagonalOutline = new PIXI.Graphics()
        const width = this.mapView.hexagonHeight * 0.02
        const color = GameColor.getColorHexValue(GameColor.Color.GrayScale25)
        const alpha = 0.9

        const initialAngle = Math.PI / 6
        hexagonalOutline.lineStyle(width, color, alpha)
        hexagonalOutline.moveTo(startX + radius * Math.cos(-initialAngle),
            startY + radius * Math.sin(-initialAngle))

        const tileEdgeCount = 6
        for(let i = 1; i <= tileEdgeCount; i++) {
            const angle = (Math.PI / 3) * i - Math.PI / 6
            hexagonalOutline.lineTo(startX + radius * Math.cos(angle),
                startY + radius * Math.sin(angle))
        }

        hexagonalOutline.closePath()
        hexagonalOutline.x = this.x
        hexagonalOutline.y = this.y
        hexagonalOutline.zIndex = this.mapView.layers.tileWhiteSurrounding
        this.whiteSurrounding = hexagonalOutline
        this.mapView.addChild(hexagonalOutline)
    }

    resetHighlight() {
        this.tileSpriteView.tint = GameColor.getColorHexValue(GameColor.Color.White)
    }

    resetHighlightWithWhiteSurrounding() {
        if(this.isStrongHighlighted) return
        this.clearHighlightWithWhiteSurrounding()
    }

    forceResetHighlightWithWhiteSurrounding() {
        this.isStrongHighlighted = false
        this.clearHighlightWithWhiteSurrounding()
    }

    private clearHighlightWithWhiteSurrounding() {
        if(this.whiteSurrounding == undefined) return

        this.resetHighlight()
        this.whiteSurrounding?.destroy()
        this.whiteSurrounding = undefined
        this.uiGameManager.eventController.unregisterUIGameAnimation(this)
        this.zIndex = this.mapView.layers.mapTiles
        this.mapView.sortChildren()
    }

    drawProbability(diceNumber: number) {
        this.removeDiceProbability()
        this.diceNumber = diceNumber
        const probabilityTexture = UIGameSpriteHandler.getProbabilityImage(diceNumber)
        if(probabilityTexture == undefined) return

        const probRadius = this.mapView.robberRadius

        const x = this.width / 2 - probRadius / 2
        const y = this.height / 2 - probRadius / 2 - probRadius / 8 + this.mapView.hexagonHeight / 5
        this.probabilityView = new UIGameMapDiceProbabilityView(x, y, probRadius, probRadius, this.hexFace, this, probabilityTexture)
        this.probabilityView.zIndex = this.mapView.layers.mapTiles
    }

    private removeDiceProbability() {
        if(this.probabilityView == undefined) return
        if(this.probabilityView.parent != undefined) this.probabilityView.parent.removeChild(this.probabilityView)
    }

    getDiceProbabilityMapPoint(): Point | undefined {
        if(this.probabilityView == undefined) return undefined
        return new Point(this.x + this.probabilityView.x, this.y + this.probabilityView.y)
    }

    private createForegroundSpriteView(foregroundTexture: PIXI.Texture): SpriteView {
        const aspectRatio = foregroundTexture.height / foregroundTexture.width
        const width = this.tileWidth * FOREGROUND_SCALE
        const height = width * aspectRatio
        const x = this.tileXOffset + (this.tileWidth - width) / 2
        const y = this.tileYOffset + this.tileWidth * FOREGROUND_Y_OFFSET_RATIO
        return new SpriteView(x, y, width, height, this, foregroundTexture)
    }

    updateTextures(backgroundTexture: PIXI.Texture, foregroundTexture?: PIXI.Texture) {
        this.tileSpriteView.updateTexture(backgroundTexture)

        if(foregroundTexture == undefined) return

        if(this.tileForegroundSpriteView != undefined) {
            this.tileForegroundSpriteView.updateTexture(foregroundTexture)
            return
        }

        this.tileForegroundSpriteView = this.createForegroundSpriteView(foregroundTexture)
    }
}
